/**
 * LiveEvent entity class
 */

import moment from 'moment'
import { formatDateByTimezone, timeStringToDate } from 'app/helpers/date'
import { showLocalizationPairs, metadataMapToLocalizations, localizationsToMetadataMap } from 'app/helpers/localization'

// Live Event localized metadata: backend verticalImage/horizontalImage map to
// the model's verticalPosterUrl (vertical) / videoPosterUrl (horizontal) fields.
const LIVE_EVENT_LOCALIZATION_PAIRS = showLocalizationPairs('verticalPosterUrl', 'videoPosterUrl')

/**
 * LiveEventFactory
 * returns LiveEvent entity class
 *
 * @class LiveEventFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.LiveEvent} class
 *
 * @ngdoc factory
 * @name mwApp.factory:LiveEventFactory
 */
/* @ngInject */
export default function LiveEventFactory ($http, $injector, Entity, Type, m2mMixin, onOffMixin, FileUploadMixin, UrlPolicy, alertLiteSvc, authSvc, storageSvc) {
  /* === LiveEvent class === */

  /**
   * LiveEvent model
   *
   * @class LiveEvent
   * @classdesc LiveEvent model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @mixes models.FileUploadMixin
   * @return {models.LiveEvent} LiveEvent entity
   */
  function LiveEvent () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  LiveEvent.prototype = Object.create(Entity.prototype)
  LiveEvent.prototype.constructor = LiveEvent

  /* === LiveEvent instance methods & fields === */

  Object.assign(LiveEvent.prototype, {

    /**
     * instance fields
     *
     * @memberof models.LiveEvent#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      ageRatingId: Number,
      description: String,
      dolbyAtmosAvailable: Boolean,
      enabled: Boolean,
      verticalPosterUrl: String,
      videoPosterUrl: String,
      localizationMetadata: {}, // map<langCode, {name,description,directors,stars,horizontalImage,verticalImage}>
      networks: Type.Stump(['Network'], ['id']),
      urls: {
        STB: 'MediaUrlDTO',
        PC: 'MediaUrlDTO',
        IOS: 'MediaUrlDTO',
        ANDROID: 'MediaUrlDTO',
        SAMSUNGTV: 'MediaUrlDTO',
        OTHERS: 'MediaUrlDTO',
        WEB: 'MediaUrlDTO'
      },
      createdTime: String,
      publishTime: String,
      unpublishTime: String,
      startTime: String,
      endTime: String,
      buyers: Number,
      contentSets: ['LiveEventSet'],
      assignedSubscriptions: ['Subscription'],
      state: String,
      catchup: {
        enabled: false,
        days: 0,
        providerType: ''
      },
      liveRewind: {
        enabled: false,
        providerType: '',
        options: [String],
        storageDurationSec: Number,
        $storageDuration: Number
      },
      defaultStream: 'DefaultStream',
      policedStreams: null, // 'PolicedStreamsFactory',
      resolution: String,
      streamPolicy: 'UrlPolicy',
      streamBindings: ['StreamBinding'],
      /* local variables */
      $scheduledPublishing: Boolean,
      $toDateUnlimited: true,
      $fromDate: Date,
      $fromTime: Date,
      $toDate: Date,
      $toTime: Date,
      $startDate: Date,
      $startTime: Date,
      $endDate: Date,
      $endTime: Date,
      $ageRatingType: 'default',
      $ageRatings: { },
      $countries: { },
      $country: {
        id: Number,
        name: String,
        isoCode: String
      },
      $ageRating: {
        id: Number,
        country: {
          id: Number,
          name: String,
          isoCode: String
        },
        sortOrder: Number,
        rating: String
      },
      $defaultAgeRating: {
        id: Number,
        country: {
          id: Number,
          name: String,
          isoCode: String
        },
        sortOrder: Number,
        rating: String
      },
      $availabilityPeriod: Date,
      $itemType: 'le',
      $itemTypeName: 'Live Event',
      $itemTypeForContentSet: 'liveEvent',
      $contentSetType: 'liveEventSet',
      $contentSetName: 'Live Event Set'
    },

    /**
     * url template
     *
     * @memberof models.LiveEvent#
     * @type {String}
     */
    _urlTemplate: 'api/live-events/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.LiveEvent#
     * @type {String}
     */
    _publicUrlTemplate: 'live-events/{{::id}}',

    /**
     * Prepare vod
     *
     * @memberof models.LiveEvent#
     * @override
     */
    _prepare: function () {
      UrlPolicy.prepareToLoadContentItem(this)

      if ((!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED')) && this.urls) {
        delete this.urls.WEB
      }

      if (this.ageRating) {
        if (!this.ageRating.country) {
          this.$ageRatingType = 'default'
          this.$defaultAgeRating = this.ageRating
          this.ageRating = {}
        } else {
          this.$ageRatingType = 'countryBased'
          this.$country = this.ageRating.country
          this.$defaultAgeRating = {}
        }
      }
      if (!this.id) {
        this.resolution = 'HD'
      }

      /* Note: set default values. */
      this.$scheduledPublishing = !!this.publishTime
      this.$toDateUnlimited = !this.unpublishTime

      this.$fromDate = this.publishTime ? formatDateByTimezone(this.publishTime, false).toDate() : moment().toDate()
      this.$toDate = this.unpublishTime ? formatDateByTimezone(this.unpublishTime, false).toDate() : this.$fromDate
      this.$fromTime = timeStringToDate(this.publishTime ? moment.utc(this.publishTime).format('HH:mm') : null, 'startOfDay')
      this.$toTime = timeStringToDate(this.unpublishTime ? moment.utc(this.unpublishTime).format('HH:mm') : null, 'endOfDay')

      this.$startDate = this.startTime ? formatDateByTimezone(this.startTime, false).toDate() : moment().toDate()
      this.$endDate = this.endTime ? formatDateByTimezone(this.endTime, false).toDate() : this.$startDate
      this.$startTime = timeStringToDate(this.startTime ? moment.utc(this.startTime).format('HH:mm') : null, 'startOfDay')
      this.$endTime = timeStringToDate(this.endTime ? moment.utc(this.endTime).format('HH:mm') : null, 'endOfDay')

      this.$ageRatings = JSON.parse(storageSvc.local.get('ageRatings'))
      this.$countries = JSON.parse(storageSvc.local.get('countries'))
      if (this.$ageRatings.defaultAgeRatings.find(dar => dar.id === this.ageRatingId)) {
        this.$ageRatingType = 'default'
        this.$defaultAgeRating = this.$ageRatings.defaultAgeRatings.find(dar => dar.id === this.ageRatingId)
      } else {
        this.$ageRatings.customAgeRatings.forEach(country => {
          if (country.ageRatings.find(ar => ar.id === this.ageRatingId)) {
            this.$ageRatingType = 'countryBased'
            this.$country = country
            this.$ageRating = country.ageRatings.find(ar => ar.id === this.ageRatingId)
          }
        })
      }
      if (this.catchup && this.catchup.days) {
        this.$availabilityPeriod = new Date(this.$startDate)
        this.$availabilityPeriod.setDate(this.$availabilityPeriod.getDate() + this.catchup.days)
      }
      if (this.liveRewind) {
        this.liveRewind.$storageDuration = this.liveRewind.storageDurationSec ? this.liveRewind.storageDurationSec / 3600 : null
      }

      // Build the directive-friendly localizations array from the backend map.
      this.localizations = metadataMapToLocalizations(this.localizationMetadata, LIVE_EVENT_LOCALIZATION_PAIRS)
    },

    /**
     * Get underlying object
     *
     * @memberof models.LiveEvent#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      UrlPolicy.prepareToSaveContentItem(this)

      const fromDate = this.$fromDate.setHours(this.$fromTime.getHours(), this.$fromTime.getMinutes(), 0)
      const toDate = this.$toDate.setHours(this.$toTime.getHours(), this.$toTime.getMinutes(), 0)
      this.publishTime = formatDateByTimezone(fromDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
      this.unpublishTime = formatDateByTimezone(toDate).format('YYYY-MM-DDTHH:mm:ss[Z]')

      const startDate = this.$startDate.setHours(this.$startTime.getHours(), this.$startTime.getMinutes(), 0)
      const endDate = this.$endDate.setHours(this.$endTime.getHours(), this.$endTime.getMinutes(), 0)
      this.startTime = formatDateByTimezone(startDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
      this.endTime = formatDateByTimezone(endDate).format('YYYY-MM-DDTHH:mm:ss[Z]')

      if (this.$toDateUnlimited) {
        this.unpublishTime = null
      }

      if (!this.$scheduledPublishing) {
        this.publishTime = null
        this.unpublishTime = null
      }
      if (this.catchup && !this.catchup.enabled && this.catchup.providerType === '') {
        this.catchup.providerType = null
      }
      if (this.liveRewind && !this.liveRewind.enabled && this.liveRewind.providerType === '') {
        this.liveRewind.providerType = null
      }

      this.ageRatingId = this.$ageRatingType === 'default' ? this.$defaultAgeRating.id : this.$ageRating.id

      // delete null urls
      if (this.urls) {
        this.urls =
          Object.keys(this.urls)
            .reduce(
              (o, platform) => {
                if (this.urls[platform] != null) {
                  o[platform] = this.urls[platform]
                }
                return o
              },
              Object.create(null)
            )
      }
      if (this.liveRewind) {
        this.liveRewind.storageDurationSec = this.liveRewind.$storageDuration ? this.liveRewind.$storageDuration * 3600 : null
      }

      // Serialize the localizations array back into the backend map shape.
      this.localizationMetadata = localizationsToMetadataMap(this.localizations, LIVE_EVENT_LOCALIZATION_PAIRS)

      return Entity.prototype._object.apply(this, arguments)
    },

    /**
     * Get image Background URL for given size
     *
     * @memberof models.LiveEvent#
     * @param  {String} fieldName
     * @param  {String} size
     * @return {String}
     */
    _urlFor: function (fieldName, size) {
      return this[fieldName]
        ? this[fieldName].indexOf('{{size}}')
          ? this[fieldName].replace('{{size}}', size)
          : this[fieldName] + '?resize=' + size
        : ''
    },

    /**
     * Patch method
     *
     * @memberof models.LiveEvent
     * @param {String} url
     * @param params
     * @return {Promise} promise
     * @private
     */
    _patch: function (url, params) {
      return $http.patch(url, params)
        .then(
          response => {
            if (response.data) {
              this._weakCopy(response.data)
            }
            return response
          }
        ).catch(
          rejection => {
            return alertLiteSvc.alertError(rejection)
          })
    },

    /**
     * On/Off Entity, performing PATCH request to the server
     *
     * @memberof models.LiveEvent
     * @param {Boolean} [enabled=this.enabled] - enable or disable Entity
     * @return {Promise} promise
     */
    _onOff: function (enabled) {
      enabled = this.enabled

      return this._patch(this._url, { enabled })
        .catch(rejection => {
          this.enabled = !this.enabled
          return rejection
        })
    }
  }, m2mMixin('networks'), FileUploadMixin)

  /* === LiveEvent static methods & fields === */

  /**
   * Generate url
   *
   * @param {Object} [o] - object over which Url will be interpolated
   * @param {String} [url]
   * @return {String}
   */
  LiveEvent.generateUrl = function (url = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    return `${cast.prototype.$_urlTemplate(o)}` + url
  }

  /**
   * Load Content sets
   *
   * @param le
   * @returns {*}
   */
  LiveEvent.loadAvailableContentSets = function (le) {
    return $http.get(`/nora/api/live-events/${le.id}/extra/content-sets`)
      .data()
      .then(data => data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  // copy all static methods from Entity class
  Object.assign(LiveEvent, Entity)
  LiveEvent._name = 'LiveEvent'
  LiveEvent.init()

  // permissions
  LiveEvent.access = {
    view: 'viewLiveEvent',
    create: 'createLiveEvent',
    edit: 'editLiveEvent',
    delete: 'deleteLiveEvent'
  }

  // extra
  LiveEvent.extra = {
    networks: ['Network'],
    playbackTokenGenerators: ['PlaybackTokenGenerator'],
    drmProfiles: ['DRMProfile'],
    liveEventSets: []
  }

  return LiveEvent
}
